import { colors, fonts, frames, glyphs } from "../../../../Styles";
import { NewButtonList, NewInputList, NewSelect, PageHeader, Setting, useAicoConnection } from "../../../../Components";
import { ButtonTypes, NewFieldStyles, NewInputListHandles, SelectFieldOption_Values } from "../../../../Definitions";
import { systemTypeOptions } from "../../../../Navigation/Menus/systemMenu";
import { useEffect, useMemo, useRef, useState } from "react";
import { Graph_QL, useSystem, useUser } from "../../../../Services";
import { useAppSelector } from "../../../../Store";
import { selectAllSystemLimitations } from "../../../../Services/Agrisys_GqlDataCaller/SystemCalls/systemLimitationsSlice";
import { SystemLimitations } from "../../../../Services/Agrisys_GqlDataCaller/SystemCalls/GQLCalls/systemLimitations";

export default function SystemLimits() {
	// #region | GraphQL
	const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
	const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);
	const { state, setDomain } = useAicoConnection();
	const { userFarms } = useUser();
	// #endregion
	
	// #region | State Limitation
	const { getSystemLimitationsInfo, maintainSystemLimitations } = useSystem(connection)
	const stateLimits = useAppSelector(selectAllSystemLimitations);
	const [localLimits, setLocalLimits] = useState<SystemLimitations[] | undefined>(undefined);
	const limitInputRef = useRef<Record<number, NewInputListHandles>>({});

	useEffect(() => {
		if (state.domain)
		getSystemLimitationsInfo(state.domain);
	}, [getSystemLimitationsInfo, state.domain]);

	// Set default values for input fields
	useEffect(() => {
		if (!localLimits) return;
	
		localLimits.forEach((l) => {
			const ref = limitInputRef.current[l.fk_system_types];
			if (!ref) return;
	
			ref.refs.current[0].setValue(l.systems_limit);
			ref.refs.current[1].setValue(l.stations_limit);
		});
	}, [localLimits]);

	useEffect(() => {
		if (stateLimits) {
			setLocalLimits(stateLimits);
		}
	}, [stateLimits]);

	// Save limits
	const UpdateStateLimits = (systemId: number) => {
		if (!stateLimits) return;
	
		const ref = limitInputRef.current[systemId];
		if (!ref) return;
	
		const config = {
			systems_limit: Number(ref.refs.current[0].value) || undefined,
			stations_limit: Number(ref.refs.current[1].value) || undefined,
		};
	
		if (!state.domain) return;
	
		maintainSystemLimitations(
			state.domain,
			systemId,
			config.systems_limit,
			config.stations_limit
		);

		setLocalLimits(prev =>
			prev?.map(item => item.fk_system_types === systemId ? {
				...item, systems_limit: config.systems_limit ?? 0, stations_limit: config.stations_limit ?? 0
			} : item )
		);
	};
	function handleDomainChange(value: SelectFieldOption_Values | undefined) {
		setDomain(value?.value ?? null);
	}

	// System Input Fields
	const SystemInputs = systemTypeOptions.map((system, index) => {
		const anyDirty = localLimits?.some(local => {
			const backend = stateLimits?.find(x => x.fk_system_types === local.fk_system_types);
			return backend && (
				local.systems_limit !== backend.systems_limit ||
				local.stations_limit !== backend.stations_limit
			);
		}) ?? false;
		
		const id = system.id;

		const local = localLimits?.find(x => x.fk_system_types === id);
		const backend = stateLimits?.find(x => x.fk_system_types === id);

		const backendSystems = backend?.systems_limit ?? undefined;
		const backendStations = backend?.stations_limit ?? undefined;

		const hasChanged = local?.systems_limit !== backendSystems || local?.stations_limit !== backendStations;

		const disabled = anyDirty && !hasChanged;

		return (
			<div key={id} style={{ display: "grid", gridTemplateColumns: "200px 280px 1fr", gap: "10px" }}>
				<div style={{ display: "flex", alignItems: "center", paddingTop: index === 0 ? "14px" : undefined, ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkSecondary }}>
					{system.label}
				</div>

				<NewInputList
					ref={(el) => { if (el) limitInputRef.current[system.id] = el }}
					placeholders={["", ""]}
					isDisabled={[disabled, disabled]}
					headers={index === 0 ? ["systems", "locations"] : undefined}
					valueOverrides={{
						valueTypes: ["number", "number"]
					}}
					fieldOverrides={{
						fieldStyle: NewFieldStyles.greyTertiary,
						fieldWidth: "137.5px"
					}}
					onChanges={[
						(systemsValue: string) => { setLocalLimits(prev => {
								if (!prev) return prev;
								return prev.map(item => item.fk_system_types === id ? { ...item, systems_limit: Number(systemsValue) || 0 } : item );
							});
						},
						(stationsValue: string) => { setLocalLimits(prev => {
								if (!prev) return prev;
								return prev.map(item => item.fk_system_types === id ? { ...item, stations_limit: Number(stationsValue) || 0 } : item );
							});
						}]}
				/>

				{hasChanged && (<div style={{ display: "flex", alignItems: "center", paddingTop: index === 0 ? "14px" : undefined, ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkSecondary }}>
					<NewButtonList
						contents={["", ""]}
						buttonType={ButtonTypes.arrowButton}
						startEnhancers={[ glyphs.Check({}), glyphs.Close({}) ]}
						onClick={() => UpdateStateLimits(id)}
					/>
				</div>)}
			</div>
		);
	})

	return (<>
		<frames.Page_inner>
			<PageHeader topHeader='Companies' subHeader='System Limits' />
			<frames._page_inner_content_scroll>
				
				<div style={{display: "flex", gap: "20px", alignItems: "center", justifyContent: "center", flexDirection: "column"}}>

					<NewSelect
						placeholder="Domain"
						fieldOverrides={{ fieldStyle: NewFieldStyles.greyDarkPrimary }}
						options={userFarms?.map(farm => ({label: farm.domain, value: farm.domain})) ?? []}
						isClearable
						isSearchable
						onChange={handleDomainChange}
						valueOverrides={{defaultValue: state.domain ? { label: state.domain, value:state.domain, id: state.domain } : undefined}}
					/>
					
					<Setting
						startEnhancer={glyphs.FarmInfo({ size: "25px" })}
						header={"Number of systems and locations"}
						content={ <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}> {SystemInputs} </div> }
					/>
				</div>
			</frames._page_inner_content_scroll>
		</frames.Page_inner>
	</>)
}